import type { Material } from '@babylonjs/core/Materials/material';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import type { BiomeId, ZoneFixture, ZonePayload } from '../../net/protocol';
import { isWalkable, tileAt, halfExtent } from './layout';

/**
 * How a zone's doors and breakables look, and how they open and break (2026-10-01). Purely
 * cosmetic, like everything in `zone/`: where they are and whether they stand is the server's
 * (`design/LEVEL-FIXTURES.md`), and this draws what the ZONE frame and the snapshot's `down` say.
 *
 * Procedural, at a fixture's budget: a door is two timber leaves between stone posts under a
 * lintel; a barrel is a banded cylinder; an urn, in the crypts, the abyss and the wastes, is three
 * stacked turned shapes. One mesh each — the parts merged with their materials — so a zone's thirty
 * barrels cost thirty draws, not ninety.
 */

/** How tall a door's leaves stand. Chosen: under the crypt walls' 2.6, over a character's 1.64. */
export const DOOR_HEIGHT = 2.4;
/** How long a door takes to swing open, seconds. */
export const DOOR_SWING_SECONDS = 0.45;
/** How long a broken barrel's shards tumble before they are gone, seconds. */
export const SHARD_SECONDS = 0.9;

const POST = 0.5;
/** A leaf's turn when open. Its sign per hinge is set in `buildDoor`, and checked by `fixtureView.test.ts`. */
const LEAF_TURN = Math.PI / 2;
const LEAF_DEPTH = 0.16;
const SHARD_GRAVITY = 14;

/** Which biomes keep urns rather than barrels. */
const URN_BIOMES: ReadonlySet<BiomeId> = new Set(['BIOME_CRYPTS', 'BIOME_ABYSS', 'BIOME_WASTES']);
/** An urn's glaze, by biome. Chosen. */
const URN_COLOUR: Partial<Record<BiomeId, [number, number, number]>> = {
  BIOME_CRYPTS: [0.5, 0.46, 0.4],
  BIOME_ABYSS: [0.3, 0.24, 0.42],
  BIOME_WASTES: [0.62, 0.36, 0.2],
};

export interface FixtureMaterials {
  /** The leaves and the barrels: timber. */
  wood: Material;
  /** The posts and lintel: the biome's own wall. */
  stone: Material;
}

/** One fixture as drawn. */
export interface FixtureVisual {
  fixture: ZoneFixture;
  /** What a click outlines and the collider is measured from. */
  parts: Mesh[];
}

interface DoorState {
  pivots: [TransformNode, TransformNode];
  /** Each leaf's open yaw. */
  swing: [number, number];
  /** 0 shut … 1 open. */
  t: number;
  opening: boolean;
}

interface Shard {
  mesh: Mesh;
  velocity: Vector3;
  spin: Vector3;
  age: number;
}

export class FixtureView {
  readonly visuals: FixtureVisual[] = [];
  private readonly root: TransformNode;
  private readonly doors = new Map<number, DoorState>();
  private readonly breakables = new Map<number, Mesh>();
  private readonly shards: Shard[] = [];
  private readonly own: StandardMaterial[] = [];
  private primed = false;

  constructor(
    private readonly scene: Scene,
    private readonly zone: ZonePayload,
    parent: TransformNode,
    private readonly materials: FixtureMaterials,
  ) {
    this.root = new TransformNode('zone-fixtures', scene);
    this.root.parent = parent;
    const urns = URN_BIOMES.has(zone.biome);
    for (const f of zone.fixtures ?? []) {
      if (f.kind === 'DOOR') this.visuals.push({ fixture: f, parts: this.buildDoor(f) });
      else this.visuals.push({ fixture: f, parts: [urns ? this.buildUrn(f) : this.buildBarrel(f)] });
    }
  }

  /**
   * Brings the drawing to `down`, and advances the swings and shards by `dt` seconds. Returns the ids
   * that stopped being click targets this call — a door that opened, a barrel that broke.
   *
   * **The first call after a build is instant**: arriving in a zone whose doors were opened and
   * barrels broken before you came is not a show of them opening and breaking.
   */
  sync(down: ReadonlySet<number>, dt: number): number[] {
    const gone: number[] = [];
    const instant = !this.primed;
    this.primed = true;
    for (const id of down) {
      const door = this.doors.get(id);
      if (door && !door.opening) {
        door.opening = true;
        if (instant) door.t = 1;
        gone.push(id);
      }
      const barrel = this.breakables.get(id);
      if (barrel) {
        this.breakables.delete(id);
        if (!instant) this.shatter(barrel);
        barrel.dispose(false, false);
        gone.push(id);
      }
    }
    for (const door of this.doors.values()) {
      if (!door.opening || door.t >= 1 && door.pivots[0].rotation.y === door.swing[0]) continue;
      door.t = Math.min(1, door.t + dt / DOOR_SWING_SECONDS);
      // Eased out: a door flung open slows as it reaches the wall.
      const e = 1 - (1 - door.t) ** 3;
      door.pivots[0].rotation.y = door.swing[0] * e;
      door.pivots[1].rotation.y = door.swing[1] * e;
    }
    this.stepShards(dt);
    return gone;
  }

  /** Whether a door is open or opening. For tests. */
  isOpen(id: number): boolean {
    return this.doors.get(id)?.opening ?? false;
  }

  /** How many shards are in the air. For tests. */
  get shardCount(): number {
    return this.shards.length;
  }

  dispose(): void {
    for (const s of this.shards) s.mesh.dispose(false, false);
    this.shards.length = 0;
    this.root.dispose(false, true);
    for (const m of this.own) m.dispose();
  }

  // ── Doors ────────────────────────────────────────────────────────────────

  private buildDoor(f: ZoneFixture): Mesh[] {
    const span = this.zone.tileSize;
    const node = new TransformNode(`door-${f.id}`, this.scene);
    node.parent = this.root;
    node.position.set(f.at.x, 0, f.at.z);
    // Local X runs along the doorway.
    node.rotation.y = f.alongX ? 0 : Math.PI / 2;

    const stone: Mesh[] = [];
    for (const side of [-1, 1]) {
      const post = MeshBuilder.CreateBox(`door-post-${f.id}`, { width: POST, height: DOOR_HEIGHT + 0.5, depth: POST }, this.scene);
      post.parent = node;
      post.position.set(side * (span / 2 - POST / 2), (DOOR_HEIGHT + 0.5) / 2, 0);
      stone.push(post);
    }
    const lintel = MeshBuilder.CreateBox(`door-lintel-${f.id}`, { width: span, height: 0.4, depth: POST + 0.1 }, this.scene);
    lintel.parent = node;
    lintel.position.set(0, DOOR_HEIGHT + 0.5 + 0.2, 0);
    stone.push(lintel);
    for (const m of stone) {
      m.material = this.materials.stone;
      m.receiveShadows = true;
      m.isPickable = false;
    }

    // Two leaves hung inside the posts, each on a pivot at its hinge. They swing toward the corridor
    // side, so an open door lies along the corridor's walls rather than over the room's.
    const leafWidth = span / 2 - POST;
    const corridorSide = this.corridorSide(f, node);
    const pivots: TransformNode[] = [];
    const leaves: Mesh[] = [];
    for (const side of [-1, 1]) {
      const pivot = new TransformNode(`door-hinge-${f.id}`, this.scene);
      pivot.parent = node;
      pivot.position.set(side * (span / 2 - POST), 0, 0);
      const leaf = MeshBuilder.CreateBox(`door-leaf-${f.id}`, { width: leafWidth, height: DOOR_HEIGHT, depth: LEAF_DEPTH }, this.scene);
      leaf.parent = pivot;
      leaf.position.set(-side * (leafWidth / 2), DOOR_HEIGHT / 2, 0);
      leaf.material = this.materials.wood;
      leaf.receiveShadows = true;
      leaf.isPickable = false;
      pivots.push(pivot);
      leaves.push(leaf);
    }
    // Each leaf turns a quarter about its hinge, its free end toward the corridor side; the two hinges
    // are mirrored, so the two turns are opposite. `fixtureView.test.ts` checks where the ends land.
    this.doors.set(f.id, {
      pivots: [pivots[0]!, pivots[1]!],
      swing: [-corridorSide * LEAF_TURN, corridorSide * LEAF_TURN],
      t: 0,
      opening: false,
    });
    return [...leaves, ...stone];
  }

  /** +1 when the corridor is on the door's local +Z side, −1 when on −Z. Asks the node, not the maths. */
  private corridorSide(f: ZoneFixture, node: TransformNode): number {
    node.computeWorldMatrix(true);
    const z = node.getDirection(Vector3.Forward());
    const half = halfExtent(this.zone);
    const corridorAt = (s: number) => {
      const x = f.at.x + z.x * s;
      const zz = f.at.z + z.z * s;
      const tile = tileAt(this.zone, Math.floor((x + half.x) / this.zone.tileSize), Math.floor((zz + half.z) / this.zone.tileSize));
      return tile.kind === 'CORRIDOR' && isWalkable(tile.kind);
    };
    return corridorAt(1) ? 1 : corridorAt(-1) ? -1 : 1;
  }

  // ── Breakables ───────────────────────────────────────────────────────────

  private buildBarrel(f: ZoneFixture): Mesh {
    const body = MeshBuilder.CreateCylinder('barrel-body', { height: 1.0, diameterTop: 0.72, diameterBottom: 0.72, tessellation: 12 }, this.scene);
    body.material = this.materials.wood;
    const iron = this.colour('barrel-iron', [0.16, 0.15, 0.14]);
    const bands = [0.18, 0.82].map((y) => {
      const band = MeshBuilder.CreateCylinder('barrel-band', { height: 0.08, diameter: 0.78, tessellation: 12 }, this.scene);
      band.position.y = y - 0.5;
      band.material = iron;
      return band;
    });
    return this.place(f, [body, ...bands], 0.5);
  }

  private buildUrn(f: ZoneFixture): Mesh {
    const glaze = this.colour('urn-glaze', URN_COLOUR[this.zone.biome] ?? [0.5, 0.45, 0.4]);
    const body = MeshBuilder.CreateCylinder('urn-body', { height: 0.5, diameterTop: 0.66, diameterBottom: 0.42, tessellation: 14 }, this.scene);
    body.position.y = 0.25;
    const shoulder = MeshBuilder.CreateCylinder('urn-shoulder', { height: 0.28, diameterTop: 0.3, diameterBottom: 0.66, tessellation: 14 }, this.scene);
    shoulder.position.y = 0.64;
    const neck = MeshBuilder.CreateCylinder('urn-neck', { height: 0.16, diameterTop: 0.36, diameterBottom: 0.26, tessellation: 14 }, this.scene);
    neck.position.y = 0.86;
    for (const m of [body, shoulder, neck]) m.material = glaze;
    return this.place(f, [body, shoulder, neck], 0);
  }

  /** Merges a breakable's parts into one mesh standing at its fixture. */
  private place(f: ZoneFixture, parts: Mesh[], lift: number): Mesh {
    const merged = Mesh.MergeMeshes(parts, true, true, undefined, false, true) ?? parts[0]!;
    merged.name = `zone-breakable-${f.id}`;
    merged.parent = this.root;
    merged.position.set(f.at.x, lift, f.at.z);
    // Turned by its id, so a cluster is not three copies facing one way.
    merged.rotation.y = (f.id * 2.399) % (Math.PI * 2);
    merged.receiveShadows = true;
    merged.isPickable = false;
    this.breakables.set(f.id, merged);
    return merged;
  }

  private colour(name: string, rgb: [number, number, number]): StandardMaterial {
    const m = new StandardMaterial(name, this.scene);
    m.diffuseColor = new Color3(...rgb);
    m.specularColor = new Color3(0.05, 0.05, 0.05);
    // The same small self-lift the rest of the world's props carry, so a barrel reads in the dark.
    m.emissiveColor = new Color3(rgb[0] * 0.15, rgb[1] * 0.15, rgb[2] * 0.15);
    this.own.push(m);
    return m;
  }

  // ── Shards ───────────────────────────────────────────────────────────────

  /** Bursts a broken barrel into tumbling pieces of its own material. */
  private shatter(barrel: Mesh): void {
    const at = barrel.getAbsolutePosition();
    const material = barrel.material && 'subMaterials' in barrel.material
      ? ((barrel.material as unknown as { subMaterials: Material[] }).subMaterials[0] ?? null)
      : barrel.material;
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + at.x;
      const size = 0.14 + ((i * 37) % 10) / 60;
      const mesh = MeshBuilder.CreateBox('barrel-shard', { width: size, height: size * 0.5, depth: size * 1.6 }, this.scene);
      mesh.material = material;
      mesh.isPickable = false;
      mesh.position.set(at.x, 0.5, at.z);
      this.shards.push({
        mesh,
        velocity: new Vector3(Math.cos(a) * 2.2, 3.5 + (i % 3), Math.sin(a) * 2.2),
        spin: new Vector3(4 + i, 3 - i, 2 + (i % 2) * 5),
        age: 0,
      });
    }
  }

  private stepShards(dt: number): void {
    for (let i = this.shards.length - 1; i >= 0; i--) {
      const s = this.shards[i]!;
      s.age += dt;
      if (s.age >= SHARD_SECONDS) {
        s.mesh.dispose(false, false);
        this.shards.splice(i, 1);
        continue;
      }
      s.velocity.y -= SHARD_GRAVITY * dt;
      s.mesh.position.addInPlace(s.velocity.scale(dt));
      if (s.mesh.position.y < 0.05) {
        s.mesh.position.y = 0.05;
        s.velocity.set(s.velocity.x * 0.4, -s.velocity.y * 0.3, s.velocity.z * 0.4);
      }
      s.mesh.rotation.addInPlace(s.spin.scale(dt));
      // Shrinks out over its last third rather than popping.
      const k = Math.min(1, (SHARD_SECONDS - s.age) / (SHARD_SECONDS / 3));
      s.mesh.scaling.setAll(k);
    }
  }
}
